import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Quote } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const testimonials = [
  {
    id: 1,
    quote: "Jérémie's expertise in technology infrastructure transformed our operations. His strategic vision and hands-on approach delivered exceptional results.",
    author: "Tech Executive",
    company: "International Corporation",
    location: "Dubai"
  },
  {
    id: 2,
    quote: "Working with Jérémie on our real estate technology integration was a game-changer. His deep understanding of both tech and business made the difference.",
    author: "Real Estate Investor",
    company: "Private Investment Group",
    location: "Singapore"
  },
  {
    id: 3,
    quote: "His Apple ecosystem expertise and cybersecurity knowledge helped us build a robust, scalable infrastructure. Highly recommended for enterprise solutions.",
    author: "CTO",
    company: "Fintech Startup",
    location: "Luxembourg"
  }
];

export const Testimonials = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.from(headerRef.current, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headerRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });

      // Cards stagger
      cardsRef.current.forEach((card, index) => {
        gsap.from(card, {
          y: 60,
          opacity: 0,
          rotateX: 10,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: index * 0.15
        });

        // Quote icon animation
        const quoteIcon = card.querySelector('.quote-icon');
        gsap.from(quoteIcon, {
          scale: 0,
          rotation: -45,
          duration: 0.5,
          ease: "back.out(2)",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none reverse"
          },
          delay: 0.3 + index * 0.15
        });
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="testimonials"
      className="py-24 md:py-32 bg-background border-t border-border/50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="mb-16 md:mb-20 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-medium block mb-4">
            Testimonials
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground">
            What People Say
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              ref={(el) => cardsRef.current[index] = el}
              className="card-3d group relative p-8 bg-concrete/30 border border-border/50 hover:bg-concrete/50 transition-colors duration-300"
            >
              {/* Quote Icon */}
              <Quote className="quote-icon w-10 h-10 text-steel/30 mb-6" />

              {/* Quote Text */}
              <p className="text-foreground/80 leading-relaxed mb-8 text-sm md:text-base">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="border-t border-border/50 pt-6">
                <p className="font-heading font-semibold text-foreground">
                  {testimonial.author}
                </p>
                <p className="text-sm text-muted-foreground">
                  {testimonial.company}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-1">
                  {testimonial.location}
                </p>
              </div>

              {/* Hover line */}
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-foreground group-hover:w-full transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
